/** Offer enums & DTOs — alineado a STOREFRONT_OFFERS.md
 *  Server-authoritative, FE nunca calcula descuento para cobro, solo display.
 *  Enums string, sin magic numbers.
 */
import type { IBudgetComboLine, ICreateOrderCombo } from './combo.types';

export enum OfferType {
  PERCENTAGE_DISCOUNT = 'PERCENTAGE_DISCOUNT',
  FIXED_AMOUNT_DISCOUNT = 'FIXED_AMOUNT_DISCOUNT',
}

export enum OfferScope {
  PRODUCT = 'PRODUCT',
  CATEGORY = 'CATEGORY',
  ALL_PRODUCTS = 'ALL_PRODUCTS',
  ORDER = 'ORDER',
}

export interface StorefrontActiveOffer {
  id: string;
  code: string;
  name: string;
  discountType: OfferType;
  discountValue: number;
  discountScope: OfferScope;
  maxDiscountAmount?: number | string | null;
  priority: number;
  startDate: string;
  endDate: string;
}

export interface StorefrontOfferListItem {
  id: string;
  code?: string;
  name: string;
  description?: string | null;
  discountType: OfferType;
  discountValue: number;
  discountScope: OfferScope;
  priority: number;
  startDate: string;
  endDate: string | null;
  isActive?: boolean;
  partnerLocationIds: string[];
  productIds?: string[];
  categoryIds?: string[];
  maxDiscountAmount?: number | string | null;
  minOrderAmount?: number | string | null;
}

// ---- Budget con descuentos ----
export interface BudgetAmountsBlock {
  subtotal: number;
  discountTotal: number;
  productDiscount: number;
  orderDiscount: number;
  deliveryCost: number;
  total: number;
}

export interface BudgetAmountsBlockLocal {
  subtotal: string;
  discountTotal: string;
  productDiscount: string;
  orderDiscount: string;
  deliveryCost: string;
  total: string;
}

export interface BudgetDiscountItem {
  productId: string;
  quantity: number;
  priceAtTime: number;
  subtotal: number;
  total: number;
  discountAmountUSD: number;
  appliedOfferId: string | null;
}

export interface BudgetOrderDiscount {
  appliedOfferId: string;
  amountSavedUSD: number;
  amountSavedLocal: string;
}

export interface BudgetCoupon {
  code: string;
  applied: boolean;
  amountSavedUSD: number;
  amountSavedLocal?: string;
}

export interface BudgetDiscounts {
  items: BudgetDiscountItem[];
  /** @deprecated usa orderDiscounts — trae solo el primer descuento de orden (ambiguo con stacking) */
  orderDiscount: BudgetOrderDiscount | null;
  /** Nuevo: lista con cupón + auto cuando stackable:true */
  orderDiscounts?: BudgetOrderDiscount[];
  /** Nuevo: null si no se envió couponCode */
  coupon?: BudgetCoupon | null;
  totalSavedUSD: number;
  totalSavedLocal: string;
}

export interface BudgetAmounts {
  usd: BudgetAmountsBlock;
  local: BudgetAmountsBlockLocal;
}

// Nuevo shape POST /public/order/budget
/** @deprecated budget legacy con delivery — ver ValidateBudgetResponse (sin delivery) */
export interface StorefrontBudgetResponse {
  currencyCode: string;
  exchangeRate: number;
  amounts: BudgetAmounts;
  discounts: BudgetDiscounts;
  items: BudgetDiscountItem[]; // compat
  // legacy fallback para transición (si backend aún devuelve shape viejo)
  subtotalAmount?: number;
  subtotalAmountConverted?: number;
  deliveryCost?: number;
  deliveryCostConverted?: number;
  totalAmount?: number;
  totalAmountConverted?: number;
}

// ---- POST /public/order/budget/validate (solo lectura, sin delivery) ----
// Introspectivo: el preview ahora vive aquí para no atar descuentos al destino/tip;
// el envío se suma aparte y el total final se compone en cliente.
export type ValidateCouponScope = 'PRODUCT' | 'CATEGORY' | 'ALL_PRODUCTS' | 'ORDER';

export interface ValidateBudgetProductInput {
  productId: string;
  quantity: number;
  attributes?: { id: string; selectedIds: string[] }[];
}

export interface ValidateBudgetRequest {
  products: ValidateBudgetProductInput[];
  combos?: ICreateOrderCombo[];
  couponCode?: string;
}

export interface ValidateBudgetLineModifier {
  groupName: string;
  name: string;
  extraPrice: number;
}

export interface ValidateBudgetLine {
  productId: string;
  quantity: number;
  priceAtTime: number;
  subtotal: number;
  total: number;
  discountAmountUSD: number;
  appliedOfferId: string | null;
  modifiers: ValidateBudgetLineModifier[];
}

export interface ValidateOrderDiscount {
  appliedOfferId: string;
  offerCode?: string;
  amountSavedUSD: number;
  amountSavedLocal: number;
}

export interface ValidateCoupon {
  code: string;
  scope: ValidateCouponScope;
  stackable: boolean;
  applied: boolean;
  amountSavedUSD: number;
  amountSavedLocal: number;
}

export interface ValidateAmountsUsd {
  subtotal: number;
  productDiscount: number;
  orderDiscount: number;
  discountTotal: number;
  total: number;
}

export interface ValidateAmountsLocal {
  subtotal: number;
  productDiscount: number;
  orderDiscount: number;
  discountTotal: number;
  total: number;
}

export interface ValidateBudgetResponse {
  currencyCode: string;
  exchangeRate: number;
  amounts: { usd: ValidateAmountsUsd; local: ValidateAmountsLocal };
  lines: ValidateBudgetLine[];
  orderDiscounts: ValidateOrderDiscount[];
  coupon: ValidateCoupon | null;
  combos?: IBudgetComboLine[];
}

export interface CouponConflictItem {
  productId: string;
  conflictingOfferId: string;
  conflictingOfferCode?: string | null;
}

/** Introspectivo: el 409 no es error fatal, es la señal para ofrecer quitar el cupón. */
export interface CouponNotStackableError {
  code: 'COUPON_NOT_STACKABLE';
  message: string;
  couponCode: string;
  conflicts: CouponConflictItem[];
}

// Snapshot en orderProducts[].attributesSnapshot.appliedOffer
export interface AppliedOfferSnapshot {
  id: string;
  name: string;
  discountType: OfferType;
  discountValue: number;
  amountSavedUSD: number;
  // compat: algunos payloads pueden traer discountScope/name/code
  discountScope?: OfferScope;
  code?: string;
  maxDiscountAmount?: number | string | null;
}
